import { useTranslation } from 'react-i18next';
import {
	Typography,
	Card,
	CardContent,
	Box,
	IconButton,
	Tabs,
	Tab,
} from '@mui/material';
import { DefaultValues, FormProvider, useForm } from 'react-hook-form';
import { useCallback, useEffect, useState } from 'react';
import AppDataGrid, {
	initialPagination,
} from '@/common/components/dataGrid/AppDataGrid';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { GridColDef } from '@mui/x-data-grid';
import qs from 'qs';
import { filterObjectByKeys, parseSummarySearchParams } from '@/utils/qsHelper';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import MeditationListSummaryFilterSection from './components/MemberProgramSummaryFilterSection';
import {
	MemberInfoFilter,
	MemberInfoPageDTO,
} from '@/api/DTO/member-management/admissionRequest';
import { useGetMemberListQuery } from '@/api/member-management/admissionRequest';
import { PermissionEnum } from '@/api/enum/common.enum';
import { useGetAllEnumListQuery } from '@/api/common';
import { useGetUser } from '@/hooks/useGetUser';
import MemberStatusChip from '@/common/components/MemberStatusChip';

export default function MemberProgramSummaryPage() {
	const { t } = useTranslation();
	const [searchParams, setSearchParams] = useSearchParams();
	const [isInitializedForm, setIsInitializedForm] = useState<boolean>(false);
	const navigate = useNavigate();
	const [currentTab, setCurrentTab] = useState('program');
	const { userInfo } = useGetUser();

	const { data: allEnumList } = useGetAllEnumListQuery(null, {
		refetchOnMountOrArgChange: true,
	});

	const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
		setCurrentTab(newValue);
		if (newValue == 'all') navigate(`/member-management/member-list`);
		if (newValue == 'case') navigate(`/member-management/member-list/case`);
		if (newValue == 'program')
			navigate(`/member-management/member-list/program`);
		if (newValue == 'noService')
			navigate(`/member-management/member-list/noService`);
	};

	const defaultPropertyFilter: MemberInfoFilter = {
		pagination: initialPagination,
		searchString: '',
		sortings: [],
		membershipStateList: [],
		responsibleProfessionalStaffIdList: [],
		responsibleProgramWorkerIdList: [],
		statusList: [],
	};
	const defaultValues: DefaultValues<MemberInfoFilter> = defaultPropertyFilter;
	const methods = useForm<MemberInfoFilter>({
		defaultValues,
	});
	const transformedSearchParams: MemberInfoFilter = {
		...defaultPropertyFilter,
		...filterObjectByKeys(parseSummarySearchParams(searchParams), [
			'pagination',
			'sortings',
			'searchString',
			'membershipStateList',
			'responsibleProfessionalStaffIdList',
			'responsibleProgramWorkerIdList',
			'statusList',
		]),
	};
	const { data: propertySummary, isSuccess: isGetPropertySummarySuccess } =
		useGetMemberListQuery(
			{ ...transformedSearchParams, type: 2 },
			{
				refetchOnMountOrArgChange: true,
			}
		);

	const transformResponse = useCallback((response?: MemberInfoPageDTO[]) => {
		if (response == null) return;
		const newResponse: any[] = [];
		response.forEach((item) => {
			newResponse.push({
				...item,
				// receivingReferralOrApplicationDate:
				// 	!item.receivingReferralOrApplicationDate
				// 		? '-'
				// 		: dayjs(item.receivingReferralOrApplicationDate)
				// 				.locale('en')
				// 				.format(DISPLAY_DATE_FORMAT),
			});
		});
		return newResponse;
	}, []);

	// watch serach params
	useEffect(() => {
		if (!isInitializedForm) {
			methods.reset(transformedSearchParams, {
				keepDefaultValues: true,
			});
			setIsInitializedForm(true);
		}
	}, [searchParams]);

	// watch fields change
	useEffect(() => {
		const subscription = methods.watch((value) => {
			const propertyFilterFormResult = value as MemberInfoFilter;

			const cleanedFilter = Object.fromEntries(
				Object.entries(propertyFilterFormResult).filter(([key, val]) => {
					return val !== '' && val != null;
				})
			);

			// append to url to reload page
			setSearchParams(qs.stringify(cleanedFilter));
		});
		return () => subscription.unsubscribe();
	}, [methods.watch]);

	const columns: GridColDef[] = [
		{
			field: 'fileNo',
			headerName: t('member.fileNo'),
			flex: 15,
		},
		{
			field: 'nameEn',
			headerName: t('member.name'),
			flex: 15,
			renderCell: (params) => (
				<Box>
					<Typography variant='body2'>{params.row.nameEn}</Typography>
					<Typography variant='body2'>{params.row.nameZh}</Typography>
				</Box>
			),
		},
		{
			field: 'phone',
			headerName: t('member.mobilePhone'),
			flex: 25,
		},

		{
			field: 'responsibleProgramWorkerName',
			headerName: t('member.responsibleProgramWorker'),
			flex: 15,
		},
		{
			field: 'responsibleProfessionalStaffName',
			headerName: t('member.responsibleProfessionalStaff'),
			flex: 15,
		},
		{
			field: 'serviceFollowDesc',
			headerName: t('member.serviceFollow'),
			flex: 15,
		},
		{
			field: 'membershipStateDesc',
			headerName: t('member.membershipState'),
			flex: 15,
		},
		{
			field: 'status',
			headerName: t('member.status'),
			flex: 15,
			maxWidth: 140,
			minWidth: 140,
			renderCell: (params) => (
				<MemberStatusChip
					status={params.row.status}
					label={params.row.statusDesc}
				/>
			),
		},
		{
			field: 'edit',
			headerName: '',
			width: 68,
			renderCell: (params) =>
				userInfo?.permissionList?.includes(
					PermissionEnum.ViewAdmissionRequestAndMember
				) && (
					<Link
						to={`/member-management/member-list/detail/${params.id}`}
						state={{
							propertySummarySearchParams: qs.stringify(
								transformedSearchParams
							),
						}}
					>
						<IconButton color='secondary'>
							<VisibilityOutlinedIcon />
						</IconButton>
					</Link>
				),
		},
	];

	return (
		<>
			<Box display='flex' justifyContent='space-between'>
				<Typography variant='h4'>
					{t('drawerListItems.memberSummary')}
				</Typography>
			</Box>

			<Card sx={{ marginTop: 3 }}>
				<CardContent>
					{' '}
					<Tabs
						value={currentTab}
						onChange={handleTabChange}
						sx={{ marginBottom: 3 }}
					>
						<Tab
							label={<Typography variant='body1'>{t('member.all')}</Typography>}
							value='all'
							sx={{ textTransform: 'none' }}
						/>
						<Tab
							label={
								<Typography variant='body1'>{t('member.case')}</Typography>
							}
							value='case'
							sx={{ textTransform: 'none' }}
						/>
						<Tab
							label={
								<Typography variant='body1'>{t('member.program')}</Typography>
							}
							value='program'
							sx={{ textTransform: 'none' }}
						/>
						<Tab
							label={
								<Typography variant='body1'>{t('member.noService')}</Typography>
							}
							value='noService'
							sx={{ textTransform: 'none' }}
						/>
					</Tabs>
					<FormProvider {...methods}>
						<MeditationListSummaryFilterSection
							allEnumList={allEnumList}
						></MeditationListSummaryFilterSection>

						<Box marginTop={2}>
							<AppDataGrid
								loading={!isGetPropertySummarySuccess}
								rows={transformResponse(propertySummary?.data?.items) ?? []}
								rowCount={propertySummary?.data?.pagination.totalItems ?? 0}
								columns={columns}
								getRowId={(row) => row.id}
							/>
						</Box>
					</FormProvider>
				</CardContent>
			</Card>
		</>
	);
}
